import React, { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { submitReview, ReviewInput } from '../../services/reviewService';
import { validateReviewInput } from '../../utils/reviewValidation';
import { useToast } from '../ui/Toast';
import StarRatingInput from './StarRatingInput';

interface ReviewFormProps {
  businessId: string;
  businessName: string;
  bookingId?: string;
  onReviewSubmitted?: () => void;
  onCancel?: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  businessId,
  businessName,
  bookingId,
  onReviewSubmitted,
  onCancel
}) => {
  const { user } = useAuthStore();
  const { success: showSuccessToast, error: showErrorToast } = useToast();

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      showErrorToast('Please log in to submit a review');
      return;
    }

    // Validate input
    const validationErrors = validateReviewInput({ rating, comment });
    if (validationErrors.length > 0) {
      showErrorToast(validationErrors.join(', '));
      return;
    }

    setIsSubmitting(true);

    try {
      const reviewData: ReviewInput = {
        business_id: businessId,
        customer_id: user.id,
        booking_id: bookingId,
        rating,
        comment: comment.trim() || undefined
      };

      const result = await submitReview(reviewData);

      if (result.success) {
        showSuccessToast('Review submitted successfully!');
        setRating(0);
        setComment('');
        onReviewSubmitted?.();
      } else {
        showErrorToast(result.error?.message || 'Failed to submit review');
      }
    } catch (err: any) {
      console.error('Review submission error:', err);
      showErrorToast('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-yellow-50 to-white rounded-2xl shadow-lg p-6 border border-yellow-200">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Write a Review
        </h3>
        <p className="text-gray-600">
          Share your experience with <span className="font-medium">{businessName}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Overall Rating *
          </label>
          <StarRatingInput
            rating={rating}
            onRatingChange={setRating}
            size="lg"
          />
          {rating > 0 && (
            <p className="mt-2 text-sm text-gray-600">
              {rating} star{rating !== 1 ? 's' : ''} - {
                rating === 5 ? 'Excellent' :
                rating === 4 ? 'Very Good' :
                rating === 3 ? 'Good' :
                rating === 2 ? 'Fair' :
                'Poor'
              }
            </p>
          )}
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Review
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
            placeholder="Tell others about your experience... (optional)"
            maxLength={1000}
          />
          <div className="mt-1 text-xs text-gray-500 text-right">
            {comment.length}/1000 characters
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || rating === 0}
            className="flex-1 px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </div>
            ) : (
              'Submit Review'
            )}
          </button>
        </div>
      </form>

      <div className="mt-4 text-xs text-gray-500 text-center">
        Your review will be published after moderation
      </div>
    </div>
  );
};

export default ReviewForm;